/**
 * Нудж «выберите активный IP-телефон на смену».
 *
 * Эталон: v4 `UsmAsteriskHtml::operPhoneSelector` — модалка, которую `DashBoardHtml.php:891`
 * поднимает ОДИН раз на сессию входа (флаг `isNeedRunDialogSelectorPhone`, выставляется
 * `UserHtml::login` при успешном логине и снимается только сохранением выбора), и только
 * если у сотрудника задан непустой `ip_phone_list`. Право отдельное не нужно — это тот же
 * самообслуживаемый `ip_phone`, что уже правится на `UsersDetailPage.tsx` (SEC-1b.2:
 * benign self-edit, `RequireEmployeeSelfOrPermission` пропускает владельца без доп.права).
 *
 * Упрощение вместо полной копии v4: флаг «показать» снимается любым закрытием модалки
 * (сохранение ИЛИ «сейчас не буду»), не только сохранением. В SPA компонент живёт весь
 * сеанс монтированным (в отличие от per-page-load MPA v4), и точная копия «показывать на
 * КАЖДОЙ навигации, пока не сохранишь» превратилась бы в липкий баннер, а не разовый нудж.
 */

import { useEffect, useState } from 'react';
import { Modal, Radio, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@features/auth/hooks/useAuth';
import { usersApi } from '../api/usersApi';
import { consumePendingFlag, parsePhoneOptions, buildPhoneOnlyPayload } from './phoneSelectorPrompt.helpers';

export function PhoneSelectorPrompt() {
  const { t } = useTranslation('users');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (user?.id && consumePendingFlag()) {
      setOpen(true);
    }
  }, [user?.id]);

  const contactQuery = useQuery({
    queryKey: ['employee-contact-phone-prompt', user?.id],
    queryFn: () => usersApi.getContact(user!.id),
    enabled: open && !!user?.id,
  });

  const contact = contactQuery.data;
  const phoneOptions = parsePhoneOptions(contact?.ip_phone_list);
  // Эталон гейтит показ модалки ДВУМЯ условиями разом: WN_ASCALL включён (сюда —
  // asterisk_enabled, вычисленный бэкендом) И список номеров непуст.
  const shouldPrompt = contact ? contact.asterisk_enabled === true && phoneOptions.length > 0 : null;

  useEffect(() => {
    if (contactQuery.isSuccess && shouldPrompt === false) {
      setOpen(false);
    }
  }, [contactQuery.isSuccess, shouldPrompt]);

  useEffect(() => {
    if (contact) {
      setSelected(contact.ip_phone && phoneOptions.includes(contact.ip_phone) ? contact.ip_phone : phoneOptions[0] ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact]);

  const updateMutation = useMutation({
    mutationFn: (phone: string) => {
      if (!contact || !user) {
        throw new Error('contact not loaded');
      }
      return usersApi.updateContact(user.id, buildPhoneOnlyPayload(contact, phone));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-contact', user?.id] });
      setOpen(false);
    },
  });

  if (!open || shouldPrompt !== true) {
    return null;
  }

  return (
    <Modal
      title={t('phoneSelector.title')}
      open={open}
      onOk={() => selected && updateMutation.mutate(selected)}
      onCancel={() => setOpen(false)}
      okText={t('phoneSelector.confirm')}
      cancelText={t('phoneSelector.skip')}
      confirmLoading={updateMutation.isPending}
      okButtonProps={{ disabled: !selected }}
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text type="secondary">{t('phoneSelector.hint')}</Typography.Text>
        <Radio.Group
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {phoneOptions.map((phone) => (
            <Radio key={phone} value={phone}>
              {phone}
            </Radio>
          ))}
        </Radio.Group>
      </Space>
    </Modal>
  );
}
